'use client';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../../lib/i18n';
import { useLayerLock } from '../../lib/useLayerLock';
import { Icon } from '../Icon';
import { won } from './price';
import { SmartLink } from '../SmartLink';
import { Working } from '../Working';

interface BookRow {
  id: number;
  type: string;
  name: string;
  price: number;
  priceOriginal: number;
  saleStatus: string;
}

const BOOK_TYPES = new Set(['LECTURE_BOOK', 'PACKAGE_BOOK', 'FREEPASS_BOOK']);

/**
 * 관련교재 모달 — 가이드 `modal` + `book-item`.
 *
 * 목록 응답에는 **개수만** 온다(카드 20장 × 교재 N개를 목록에 실을 이유가 없다).
 * 그래서 열 때 상세(`/products/:id`)를 받아 교재 매핑만 골라 그린다 — 현행도 버튼만 띄우고
 * 목록은 모달에서 따로 받았다.
 *
 * 닫기는 **X·닫기 버튼과 Esc 만**이다. 딤을 눌러도 닫히지 않는다(가이드 스크립트는 딤 클릭으로
 * 닫지만, 잘못 눌러 닫히면 다시 찾아 열어야 하므로 프로젝트 규칙을 따른다).
 *
 * 레이어는 **body 로 내보낸다**(portal). 링크가 `course-thumb` 안에 있고 그 상자는
 * `overflow:hidden` 이라, 같은 자리에 그리면 잘릴 수 있다 — 가이드도 모달을 body 끝에 둔다.
 *
 * 가이드에 있는 저자 표기(`book-author`)는 넣지 않았다 — 상품에 저자 칸이 없다.
 */
export function RelatedBooksModal({ productId, count, teacherName, className, iconOnly }: {
  productId: number;
  count: number;
  teacherName?: string | null;
  /** 링크 모양 — 카드는 `course-link`, 리스트는 가이드가 배지 모양으로 둔다 */
  className?: string;
  /** 글자 없이 아이콘만 — 카드형이 쓴다(뜻은 `title`·`aria-label` 이 진다) */
  iconOnly?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<BookRow[] | null>(null);
  /** portal 은 마운트 뒤에만 — 서버 렌더에는 document 가 없다 */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useLayerLock(open, () => setOpen(false));

  useEffect(() => {
    if (!open || rows) return;
    fetch(`/api/products/${productId}`)
      .then((r) => r.json())
      .then((d: { childMappings?: { type: string; child: BookRow }[] }) =>
        setRows((d.childMappings ?? []).filter((m) => BOOK_TYPES.has(m.type)).map((m) => m.child)))
      .catch(() => setRows([]));
  }, [open, rows, productId]);

  if (count <= 0) return null;

  const layer = (
    <>
      {/* 딤 클릭으로는 닫지 않는다 */}
      <div className="modal-backdrop open"></div>
      <div className="modal open">
        <div className="modal-header">
          <h3 className="modal-title">{t('prd.relatedBooks')}</h3>
          <button className="modal-close" onClick={() => setOpen(false)} aria-label={t('action.close')}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">
          {teacherName && (
            <p className="course-teacher mb15"><Icon name="user" /> {teacherName}</p>
          )}
          {rows == null && <Working block message={t('common.loading')} note={t('common.loadingNote')} />}
          {rows?.length === 0 && <p className="form-hint">{t('prd.noRelatedBooks')}</p>}
          {rows?.map((b) => (
            <div className="book-item" key={b.id}>
              <div className="book-thumb"><div className="book-thumb-no-img"><Icon name="book" /></div></div>
              <div className="book-info">
                <p className="book-title">{b.name}</p>
                <p className="book-price">{won(b.price)}{t('prd.money')}</p>
              </div>
              <SmartLink className="action-btn action-submit btn-sm" href={`/books/${b.id}`}>{t('prd.viewDetail')}</SmartLink>
            </div>
          ))}
        </div>
        <div className="layer-footer">
          <button className="action-btn action-cancel btn-sm" onClick={() => setOpen(false)}>
            {t('action.close')}
          </button>
        </div>
      </div>
    </>
  );


  return (
    <>
      <a href="#" className={className ?? 'course-link'}
        title={t('prd.relatedBooks')}
        aria-label={iconOnly ? t('prd.relatedBooks') : undefined}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
        {/* 링크는 `book-open` 이다 — 가이드가 자리를 가른다(썸네일 빈 자리만 `book`) */}
        <Icon name="book-open" />{iconOnly ? null : <> {t('prd.relatedBooks')}</>}
      </a>
      {open && mounted && createPortal(layer, document.body)}
    </>
  );
}
